import { useTranslation } from 'react-i18next';
import { cx } from '../../../lib/cx';
import { formatMin, MINUTES_PER_DAY } from '@wiztime/core';
import { useDayUi } from '../dayUiStore';
import { axisLabels, isCoveredByNowChip, labelStepHours, percentOf } from './laneModel';
import styles from './Timeline.module.css';

/** An axis label this close (in pixels) to a drag pill is hidden (ui-redesign §3.4.5). */
const PILL_CLEARANCE_PX = 14;

interface TimeAxisProps {
  readonly dayStartMin: number;
  readonly laneHeightPx: number;
  /** Wall minute of "now" when it lies in this day; shown as a chip on the axis. */
  readonly nowMin: number | null;
}

/**
 * The 52px column of hour labels; the label at 00:00 reads "next day". A label the "now" chip would
 * cover is hidden: the chip shows the time itself. While a block is dragged, accent pills mark the
 * start and end of the preview; hovering empty lane space marks the ghost's start with a faint pill.
 */
export function TimeAxis({ dayStartMin, laneHeightPx, nowMin }: TimeAxisProps) {
  const { t } = useTranslation();
  const dragSpan = useDayUi((state) => state.dragSpan);
  const hoverMin = useDayUi((state) => state.hoverMin);
  const labels = axisLabels(dayStartMin, labelStepHours(laneHeightPx));
  const pillMins = dragSpan !== null ? [dragSpan.startMin, dragSpan.endMin] : [];
  const coveredByPill = (labelMin: number): boolean =>
    pillMins.some((min) => (Math.abs(labelMin - min) / MINUTES_PER_DAY) * laneHeightPx < PILL_CLEARANCE_PX);
  return (
    <div className={styles.axis} aria-hidden="true" data-testid="time-axis">
      {labels.map((label) => (
        <span
          key={label.min}
          className={cx(styles.axisLabel, label.nextDay && styles.axisNextDay)}
          style={{
            top: `${percentOf(label.min, dayStartMin)}%`,
            visibility:
              isCoveredByNowChip(label.min, nowMin, laneHeightPx) || coveredByPill(label.min)
                ? 'hidden'
                : undefined,
          }}
          data-testid={label.nextDay ? 'axis-next-day' : undefined}
        >
          {label.text}
          {label.nextDay ? <span className={styles.nextDay}>{t('timeline.nextDay')}</span> : null}
        </span>
      ))}
      {pillMins.map((min, index) => (
        <span
          key={index === 0 ? 'start' : 'end'}
          className={styles.axisPill}
          style={{ top: `${percentOf(min, dayStartMin)}%` }}
          data-testid="axis-pill"
        >
          {formatMin(min)}
        </span>
      ))}
      {dragSpan === null && hoverMin !== null ? (
        <span
          className={cx(styles.axisPill, styles.axisPillSoft)}
          style={{ top: `${percentOf(hoverMin, dayStartMin)}%` }}
          data-testid="axis-pill-soft"
        >
          {formatMin(hoverMin)}
        </span>
      ) : null}
      {nowMin !== null ? (
        <span className={styles.axisNow} style={{ top: `${percentOf(nowMin, dayStartMin)}%` }}>
          {formatMin(nowMin)}
        </span>
      ) : null}
    </div>
  );
}
